import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Container, Typography, Box, Chip, Alert, Paper, Card, CardContent, Grid, Button
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

/**
 * Reconciliation Dashboard — admin page over the existing reconciliation
 * read endpoints (999.652 R4). Mirrors FinancialLedger.jsx's structure
 * (summary cards + AdminTable over billingApi) — the closest sibling admin
 * report page in this frontend.
 *
 * Backend: GET /api/admin/reconciliation (history), GET
 * /api/admin/reconciliation/latest (most recent report), POST
 * /api/admin/reconciliation/run (manual trigger) — all mounted under
 * admin.routes.js's router-level `authenticateJWT, requireAdmin` guard, same
 * as every other admin page (no new auth wiring needed).
 */

const STATUS_CONFIG = {
  clean:        { label: 'Clean',          color: 'success' },
  issues_found: { label: 'Issues Found',   color: 'error' },
  error:        { label: 'Provider Error', color: 'warning' },
};

function statusConfig(status) {
  return STATUS_CONFIG[status] || { label: status || 'Unknown', color: 'default' };
}

export default function ReconciliationDashboard() {
  const columns = useMemo(() => [
    {
      field: 'run_at',
      headerName: 'Run At',
      width: 200,
      type: 'date',
      renderCell: (row) => formatDate(row.run_at)
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      type: 'singleSelect',
      filterOptions: ['clean', 'issues_found', 'error'],
      renderCell: (row) => {
        const cfg = statusConfig(row.status);
        return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
      }
    },
    { field: 'total_db', headerName: 'DB Subs', width: 110, type: 'numeric' },
    { field: 'total_provider', headerName: 'Provider Subs', width: 130, type: 'numeric' },
    { field: 'orphaned_count', headerName: 'Orphaned', width: 110, type: 'numeric' },
    { field: 'untracked_count', headerName: 'Untracked', width: 110, type: 'numeric' },
    { field: 'mismatch_count', headerName: 'Mismatches', width: 120, type: 'numeric' },
  ], []);

  const [reports, setReports] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [listData, latestData] = await Promise.all([
        billingApi.get('/api/admin/reconciliation?limit=50'),
        billingApi.get('/api/admin/reconciliation/latest'),
      ]);

      setReports(listData.reports || []);
      setLatest(latestData.report || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleRunNow() {
    try {
      setRunning(true);
      setError('');
      await billingApi.post('/api/admin/reconciliation/run', {});
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }

  if (loading && reports.length === 0) {
    return <AdminPageSkeleton />;
  }

  const latestCfg = latest ? statusConfig(latest.status) : null;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4">Reconciliation</Typography>
        <Button size="small" variant="outlined" onClick={handleRunNow} disabled={running}>
          {running ? 'Running…' : 'Run Now'}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compares subscription records against the payment provider. Runs automatically once a day;
        an imbalance also writes an audit-log alert and an ERROR-severity log entry.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {latest && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Latest Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={latestCfg.label} color={latestCfg.color} size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Orphaned
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{latest.orphaned_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Untracked
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{latest.untracked_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Mismatches
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{latest.mismatch_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper variant="outlined" sx={{ height: 600 }}>
        <AdminTable
          rows={reports}
          columns={columns}
          defaultSort={[{ field: 'run_at', direction: 'desc' }]}
        />
      </Paper>
    </Container>
  );
}
